import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from "./ui/radio-group"
import { Label } from "./ui/label"

// Common Stage B suggestion format
interface Suggestion {
    originalTerm: string;
    suggestedTerm: string;
    page: number;
    context: any;
}

// Stage C group suggestion format
interface GroupSuggestion {
    type: 'merge' | 'subentry' | string;
    terms: string[];
    suggested_main: string;
    reason: string;
}

interface ReviewDecision extends Suggestion {
    decision: 'accept' | 'reject' | 'edit';
    editedTerm?: string;
}

// For Stage C
interface GroupReviewDecision {
    type: 'merge' | 'subentry' | string;
    terms: string[];
    suggested_main: string;
    reason: string;
    decision: 'accept' | 'reject' | 'edit';
    editedMain?: string;
}

interface ReviewTermsProps {
    workflowId: string;
    stage: 'B' | 'C' | 'D'; 
    onSubmitReview: (decisions: ReviewDecision[] | GroupReviewDecision[]) => Promise<void>;
}

export const ReviewTerms: React.FC<ReviewTermsProps> = ({ workflowId, stage, onSubmitReview }) => {
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [groupSuggestions, setGroupSuggestions] = useState<GroupSuggestion[]>([]);
    const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>({});
    const [groupDecisions, setGroupDecisions] = useState<Record<string, GroupReviewDecision>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rawData, setRawData] = useState<string | null>(null);
    const [uniqueTerms, setUniqueTerms] = useState<string[]>([]);

    const isStageC = stage === 'C';

    useEffect(() => {
        const fetchReviewData = async () => {
            setLoading(true);
            setError(null);
            
            // Track retry attempts
            let retryCount = 0;
            const maxRetries = 5; // Increased from 3 to 5
            
            const attemptFetch = async () => {
                try {
                    console.log(`Fetching review data for: ${workflowId}, stage: ${stage}, attempt: ${retryCount + 1}`);
                    
                    // Use fetch with timeout to prevent hanging requests
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 30000); // Increased from 15s to 30s timeout
                    
                    const { getApiUrl } = await import('../lib/config');
                    const response = await fetch(getApiUrl(`/api/workflow/${workflowId}/review-data/${stage}`), {
                        signal: controller.signal,
                        headers: {
                            'Accept': 'application/json',
                            'Cache-Control': 'no-cache' // Prevent caching issues
                        }
                    }).catch(fetchError => {
                        clearTimeout(timeoutId);
                        console.error("Network error:", fetchError);
                        throw fetchError;
                    });
                    
                    clearTimeout(timeoutId);
                    
                    if (!response) {
                        throw new Error("Network error - check server connection");
                    }
                    
                    const rawText = await response.text().catch(textError => {
                        console.error("Error reading response text:", textError);
                        return null;
                    });

                    if (!rawText) {
                        throw new Error("Empty response from server");
                    }
                    
                    setRawData(rawText);
                    console.log("Raw response:", rawText);
                    
                    // Process the response data
                    const processed = processResponseData(rawText, response.ok);
                    
                    // If processing succeeded but no suggestions found, retry
                    if (processed === false && retryCount < maxRetries) {
                        retryCount++;
                        console.log(`No data found, automatic retry attempt ${retryCount} for stage ${stage}...`);
                        // Exponential backoff for retries
                        const delay = 1000 * Math.pow(2, retryCount-1);  
                        await new Promise(resolve => setTimeout(resolve, delay));
                        return attemptFetch(); // Retry
                    }
                    
                    return processed;
                    
                } catch (err) {
                    if (retryCount < maxRetries) {
                        retryCount++;
                        console.log(`Retry attempt ${retryCount} for stage ${stage}...`);
                        // Exponential backoff for retries
                        const delay = 1000 * Math.pow(2, retryCount-1);  
                        await new Promise(resolve => setTimeout(resolve, delay));
                        return attemptFetch(); // Retry
                    }
                    throw err;
                }
            };
            
            // Process the response data once fetched
            const processResponseData = (rawText: string, isResponseOk: boolean) => {
                try {
                    const parsedData = JSON.parse(rawText);
                    console.log("Parsed data:", parsedData);
                    
                    // DETECT STAGE C FORMAT
                    if (isStageC && parsedData && parsedData.suggestions && Array.isArray(parsedData.suggestions)) {
                        // Handle Stage C format
                        const groupSuggestionsArray = parsedData.suggestions as GroupSuggestion[];
                        console.log(`Found ${groupSuggestionsArray.length} group suggestions for Stage C`);
                        
                        // Store unique terms if available
                        if (parsedData.uniqueTerms && Array.isArray(parsedData.uniqueTerms)) {
                            setUniqueTerms(parsedData.uniqueTerms);
                        }
                        
                        setGroupSuggestions(groupSuggestionsArray);
                        
                        // Initialize group decisions
                        const initialGroupDecisions: Record<string, GroupReviewDecision> = {};
                        groupSuggestionsArray.forEach((s, index) => {
                            const key = `group-${index}`; // Create a unique key
                            initialGroupDecisions[key] = { 
                                ...s, 
                                decision: 'accept',
                                editedMain: ''
                            };
                        });
                        setGroupDecisions(initialGroupDecisions);
                        setError(null);
                        
                        return true; // Successfully processed
                    }
                    
                    // HANDLE STAGE B FORMAT
                    let suggestionsArray: Suggestion[] = [];
                    
                    if (Array.isArray(parsedData)) {
                        // Direct array of suggestions
                        suggestionsArray = parsedData;
                    } else if (parsedData && typeof parsedData === 'object') {
                        // Handle object with key-value pairs (original → suggested)
                        if (typeof parsedData === 'object' && !Array.isArray(parsedData) && !parsedData.suggestions) {
                            suggestionsArray = Object.entries(parsedData).map(([original, suggested]) => ({
                                originalTerm: original,
                                suggestedTerm: suggested as string,
                                page: 1, // Default page if not provided
                                context: ""
                            }));
                        } 
                        // Object with suggestions property
                        else if (Array.isArray(parsedData.suggestions)) {
                            suggestionsArray = parsedData.suggestions;
                        } else {
                            // Try to extract suggestions some other way
                            const possibleSuggestions = Object.values(parsedData).find(Array.isArray);
                            if (possibleSuggestions) {
                                suggestionsArray = possibleSuggestions as Suggestion[];
                            }
                        }
                    }
                    
                    console.log(`Found ${suggestionsArray.length} suggestions for Stage ${stage}`);
                    
                    if (suggestionsArray.length > 0) {
                        setSuggestions(suggestionsArray);
                        
                        // Initialize decisions
                        const initialDecisions: Record<string, ReviewDecision> = {};
                        suggestionsArray.forEach((s, index) => {
                            const key = `${s.originalTerm}-${index}`; // Create a unique key
                            initialDecisions[key] = { ...s, decision: 'accept' }; // Default to accept
                        });
                        setDecisions(initialDecisions);
                        setError(null); // Clear any previous errors
                        return true; // Successfully processed
                    } else if (!isResponseOk) {
                        // Only show error if we couldn't get suggestions AND response was not ok
                        throw new Error(`Failed to fetch review data: Server returned an error`);
                    }
                    
                    return false; // No suggestions, but not an error
                } catch (parseError) {
                    console.error("JSON parse error:", parseError);
                    throw new Error(`Failed to process review data: ${parseError}`);
                }
            };
            
            try {
                // Start the fetch process with retry logic
                await attemptFetch();
            } catch (err: any) {
                console.error("Error processing review data:", err);
                setError(err.message || 'Could not load terms for review.');
                
                // Try to extract any valid data from rawData even if there was an error
                if (rawData) {
                    try {
                        processResponseData(rawData, true);
                    } catch (fallbackError) {
                        console.error("Fallback data processing failed:", fallbackError);
                    }
                }
            } finally {
                setLoading(false);
            }
        };
        
        fetchReviewData();
    }, [workflowId, stage, isStageC]);

    // -------------- HANDLERS FOR STAGE B ---------------
    const handleDecisionChange = (key: string, decision: 'accept' | 'reject' | 'edit') => {
        setDecisions(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                decision,
                // Reset edited term if decision is not 'edit'
                editedTerm: decision !== 'edit' ? '' : prev[key]?.editedTerm
            }
        }));
    };

    const handleEditChange = (key: string, e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDecisions(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                editedTerm: e.target.value,
                // Update the displayed suggested term to show edit is active
                suggestedTerm: prev[key].decision === 'edit' ? e.target.value : prev[key].suggestedTerm
            }
        }));
    };

    // -------------- HANDLERS FOR STAGE C ---------------
    const handleGroupDecisionChange = (key: string, decision: 'accept' | 'reject' | 'edit') => {
        setGroupDecisions(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                decision,
                // Reset edited main if decision is not 'edit'
                editedMain: decision !== 'edit' ? '' : prev[key]?.editedMain
            }
        }));
    };

    const handleGroupEditChange = (key: string, e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setGroupDecisions(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                editedMain: e.target.value,
                // Update the displayed suggested main to show edit is active
                suggested_main: prev[key].decision === 'edit' ? e.target.value : prev[key].suggested_main
            }
        }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);
        let retryCount = 0;
        const maxRetries = 2;

        const attemptSubmit = async (): Promise<boolean> => {
            try {
                console.log(`[${stage}] Submitting review, attempt ${retryCount + 1}...`);
                
                let formattedDecisions;
                if (isStageC) {
                    // Format Stage C decisions for submission
                    formattedDecisions = Object.values(groupDecisions).map(d => {
                        return {
                            ...d,
                            // If edited, use edited value, otherwise use suggested_main
                            suggested_main: d.decision === 'edit' && d.editedMain ? d.editedMain : d.suggested_main
                        };
                    });
                } else {
                    // Format Stage B decisions for submission
                    formattedDecisions = Object.values(decisions).map(d => {
                        return {
                            ...d,
                            // If edited, use edited value, otherwise use suggestedTerm
                            suggestedTerm: d.decision === 'edit' && d.editedTerm ? d.editedTerm : d.suggestedTerm
                        };
                    });
                }
                
                // Use timeout to prevent hanging submissions
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout
                
                try {
                    await onSubmitReview(formattedDecisions);
                    clearTimeout(timeoutId);
                    return true; // Success
                } catch (err: any) {
                    clearTimeout(timeoutId);
                    console.error(`Submit error: ${err.message}`);
                    throw err;
                }
            } catch (err: any) {
                if (retryCount < maxRetries) {
                    retryCount++;
                    console.log(`Retrying submission, attempt ${retryCount + 1}`);
                    // Exponential backoff
                    const delay = 1000 * Math.pow(2, retryCount);
                    await new Promise(resolve => setTimeout(resolve, delay));
                    return attemptSubmit(); // Retry
                }
                throw err;
            }
        };
        
        try {
            const success = await attemptSubmit();
            if (success) {
                console.log(`Successfully submitted ${stage} review decisions`);
            }
        } catch (err: any) {
            console.error("Final submission error:", err);
            setError(err.message || 'Failed to submit review.');
            
            // Also display an alert in case the error state isn't noticed
            if (stage === 'B') {
                alert(`Failed to submit review: ${err.message}\n\nDon't worry! Your review choices are still saved in this form. Try submitting again in a moment.`);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <p className="text-slate-600">Loading review terms...</p>;
    
    // For Stage C, check if we have group suggestions
    if (isStageC && groupSuggestions.length === 0) {
        return (
            <div className="text-red-600">
                <p className="mb-2">Error Loading Review Data: {error || 'No term groups found for review'}</p>
                <Button 
                    variant="outline" 
                    onClick={async () => {
                        setLoading(true);
                        setError(null);
                        if (workflowId) {
                            // Force bypass any potential caching
                            const { getApiUrl: _getApiUrl } = await import('../lib/config');
                            fetch(_getApiUrl(`/api/workflow/${workflowId}/review-data/${stage}?t=${Date.now()}`), {
                                headers: {
                                    'Accept': 'application/json',
                                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                                    'Pragma': 'no-cache'
                                }
                            })
                            .then(response => response.text())
                            .then(rawText => {
                                setRawData(rawText);
                                try {
                                    const parsedData = JSON.parse(rawText);
                                    if (parsedData && parsedData.suggestions && Array.isArray(parsedData.suggestions)) {
                                        setGroupSuggestions(parsedData.suggestions);
                                        // Initialize group decisions
                                        const initialGroupDecisions: Record<string, GroupReviewDecision> = {};
                                        parsedData.suggestions.forEach((s: GroupSuggestion, index: number) => {
                                            const key = `group-${index}`;
                                            initialGroupDecisions[key] = { 
                                                ...s, 
                                                decision: 'accept',
                                                editedMain: ''
                                            };
                                        });
                                        setGroupDecisions(initialGroupDecisions);
                                        if (parsedData.uniqueTerms && Array.isArray(parsedData.uniqueTerms)) {
                                            setUniqueTerms(parsedData.uniqueTerms);
                                        }
                                        setError(null);
                                    } else {
                                        throw new Error("Invalid data format");
                                    }
                                } catch (err: any) {
                                    setError(`Data format error: ${err.message}`);
                                }
                            })
                            .catch(err => {
                                setError(`Failed to reload: ${err.message}`);
                            })
                            .finally(() => {
                                setLoading(false);
                            });
                        }
                    }}
                    className="mt-2 bg-blue-50 hover:bg-blue-100"
                >
                    ↻ Try Again Now
                </Button>
                {rawData && (
                    <details className="text-xs mt-2">
                        <summary>Show Raw Response</summary>
                        <pre className="bg-slate-100 p-2 rounded">{rawData}</pre>
                    </details>
                )}
            </div>
        );
    }
    
    // For other stages, check if we have regular suggestions
    if (!isStageC && suggestions.length === 0) {
        return (
            <div className="text-red-600">
                <p className="mb-2">Error: {error || 'No terms found for review'}</p>
                {rawData && (
                    <details className="text-xs mt-2">
                        <summary>Show Raw Response</summary>
                        <pre className="bg-slate-100 p-2 rounded">{rawData}</pre>
                    </details>
                )}
            </div>
        );
    }

    // RENDER STAGE C UI
    if (isStageC) {
        return (
            <div className="space-y-4 mt-6 mb-6">
                <h4 className="text-md font-semibold text-blue-800">Term Group Review (Stage C)</h4>
                {error && (
                    <div className="bg-yellow-50 border-yellow-200 border p-2 rounded text-yellow-700 text-sm mb-4">
                        Warning: {error} - Showing available data despite error.
                    </div>
                )}
                <p className="text-sm text-slate-600 mb-4">
                    Review the suggested term groupings below. Each group contains related terms.
                </p>
                
                {Object.entries(groupDecisions).map(([key, group]) => (
                    <Card key={key} className="p-4 border-slate-200 mb-4">
                        <CardContent className="p-0">
                            <div className="mb-3">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="text-md font-semibold">
                                        {group.type === 'merge' ? 'Merge Terms' : 'Create Subentries'}
                                    </h4>
                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                        group.type === 'merge' 
                                            ? 'bg-blue-100 text-blue-800' 
                                            : 'bg-purple-100 text-purple-800'
                                    }`}>
                                        {group.type}
                                    </span>
                                </div>
                                
                                <p className="text-sm mb-2 text-slate-600">
                                    <span className="font-medium">Reason:</span> {group.reason}
                                </p>
                                
                                <div className="mt-3 mb-3">
                                    <p className="text-sm font-medium mb-1">Terms in this group:</p>
                                    <ul className="list-disc pl-5 text-sm space-y-1">
                                        {group.terms.map((term, i) => (
                                            <li key={i} className="text-slate-800">{term}</li>
                                        ))}
                                    </ul>
                                </div>
                                
                                <div className="mt-3 p-2 bg-blue-50 rounded">
                                    <p className="text-sm font-medium mb-1">
                                        {group.type === 'subentry' ? 'Suggested Main Entry:' : 'Suggested Main Term:'}
                                    </p>
                                    <p className={`font-semibold ${
                                        group.type === 'merge' ? 'text-blue-700' : 'text-purple-700'
                                    }`}>
                                        {group.decision === 'edit' && group.editedMain ? group.editedMain : group.suggested_main}
                                    </p>
                                </div>
                                
                                <div className="flex space-x-2 mt-3">
                                    <Button 
                                        variant={group.decision === 'accept' ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => handleGroupDecisionChange(key, 'accept')}
                                        className="text-xs py-1 h-8"
                                    >
                                        Accept
                                    </Button>
                                    <Button 
                                        variant={group.decision === 'reject' ? "ghost" : "outline"} 
                                        size="sm"
                                        onClick={() => handleGroupDecisionChange(key, 'reject')}
                                        className="text-xs py-1 h-8 text-red-500"
                                    >
                                        Reject
                                    </Button>
                                    <Button 
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleGroupDecisionChange(key, 'edit')}
                                        className={`text-xs py-1 h-8 ${group.decision === 'edit' ? 'bg-slate-100' : ''}`}
                                    >
                                        Edit Main Term
                                    </Button>
                                </div>
                                
                                {group.decision === 'edit' && (
                                    <Textarea
                                        placeholder="Enter custom main term..."
                                        value={group.editedMain || ''}
                                        onChange={(e) => handleGroupEditChange(key, e)}
                                        className="text-sm mt-2"
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
                
                <Button 
                    onClick={handleSubmit} 
                    disabled={isSubmitting}
                    className="mt-4"
                >
                    {isSubmitting ? 'Submitting...' : 'Submit Review & Continue Workflow'}
                </Button>
                
                {uniqueTerms.length > 0 && (
                    <details className="mt-4 text-sm">
                        <summary className="cursor-pointer">View All Terms ({uniqueTerms.length})</summary>
                        <div className="mt-2 p-3 bg-slate-50 rounded max-h-60 overflow-y-auto">
                            <ul className="list-disc pl-5 text-sm">
                                {uniqueTerms.map((term, i) => (
                                    <li key={i} className="text-slate-600">{term}</li>
                                ))}
                            </ul>
                        </div>
                    </details>
                )}
            </div>
        );
    }

    // RENDER STAGE B UI
    return (
        <div className="space-y-4 mt-6 mb-6">
            <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 border border-purple-500/20 rounded-lg p-4 mb-6">
                <h4 className="text-lg font-semibold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                    Manual Review Needed (Stage {stage})
                </h4>
                <p className="text-sm text-muted-foreground mt-2">
                    Review the suggested standardized terms below. Approve or reject each suggestion.
                </p>
            </div>
            {error && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-orange-500 text-sm mb-4">
                    <span className="font-medium">Warning:</span> {error} - Showing available data despite error.
                </div>
            )}
            {Object.entries(decisions).map(([key, decisionData]) => (
                <Card key={key} className="bg-card border-border hover:border-purple-500/30 transition-colors mb-3">
                    <CardContent className="p-4">
                        <div className="flex flex-wrap justify-between items-start">
                            <div className="mb-3 flex-grow">
                                <p className="text-sm mb-2">
                                    <span className="font-medium text-muted-foreground">Original:</span> 
                                    <span className="ml-2 text-foreground">{decisionData.originalTerm}</span>
                                </p>
                                <p className="text-sm mb-2">
                                    <span className="font-medium text-muted-foreground">Suggested:</span> 
                                    <span className="ml-2 text-purple-600 font-medium">{
                                        // Show edited term if in edit mode and editedTerm exists
                                        decisionData.decision === 'edit' && decisionData.editedTerm 
                                            ? decisionData.editedTerm 
                                            : decisionData.suggestedTerm
                                    }</span>
                                </p>
                                {decisionData.context && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        <span className="font-medium">Context:</span> {decisionData.context}
                                    </p>
                                )}
                            </div>
                            <div className="flex space-x-2">
                                <Button 
                                    variant={decisionData.decision === 'accept' ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handleDecisionChange(key, 'accept')}
                                    className={`text-xs py-1 h-8 ${decisionData.decision === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-green-50 hover:text-green-700 hover:border-green-300'}`}
                                >
                                    Approve
                                </Button>
                                <Button 
                                    variant={decisionData.decision === 'reject' ? "ghost" : "outline"} 
                                    size="sm"
                                    onClick={() => handleDecisionChange(key, 'reject')}
                                    className={`text-xs py-1 h-8 ${decisionData.decision === 'reject' ? 'bg-red-500 hover:bg-red-600 text-white' : 'text-red-500 hover:bg-red-50 hover:border-red-300'}`}
                                >
                                    Reject
                                </Button>
                                <Button 
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDecisionChange(key, 'edit')}
                                    className={`text-xs py-1 h-8 ${decisionData.decision === 'edit' ? 'bg-purple-100 border-purple-300 text-purple-700' : 'hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300'}`}
                                >
                                    Edit
                                </Button>
                            </div>
                        </div>

                        {decisionData.decision === 'edit' && (
                            <Textarea
                                placeholder="Enter correct term..."
                                value={decisionData.editedTerm || ''}
                                onChange={(e) => handleEditChange(key, e)}
                                className="text-sm mt-2"
                            />
                        )}
                    </CardContent>
                </Card>
            ))}
            <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting}
                className="mt-2"
            >
                {isSubmitting ? 'Submitting...' : 'Submit Review & Continue Workflow'}
            </Button>
        </div>
    );
}; 